import {
  useReducer, useEffect, useRef, lazy,
  useCallback, useContext,
} from 'react';
import { isEqual } from 'lodash';
import { useDispatch, useListeners } from '@client/hooks/redux.hooks';
import { startCrop, startUpload } from '@client/slices/features/uploads.reducer';
import { useSetFieldValue } from '@client/hooks/formik.hooks';
import { Modal } from '@client/components/globals/Modal';
import { Loader } from '@client/components/common/Loader';
import { AutosaveContext } from '../common/AutosaveContext';
import { UploadReducer } from './UploadReducer';
import { uploadsListeners } from './uploads.listeners';
import type { Crop, ImageUpload } from '@sda/interfaces/reducer/uploads.reducer';
import type { PublicImageUrl } from '@sda/interfaces/image';

interface ImageUploadHookProps {
  readonly name: string;
  readonly value: PublicImageUrl[];
  readonly disabled?: boolean;
  readonly multiple?: boolean;
  readonly targetRatio?: number;
}

const CropDialogLoader = lazy(() => import(/* webpackPrefetch: true */ './CropDialog'));

export const useImageUpload = ({
  name, value,
  disabled = false,
  multiple = true,
  targetRatio = 16 / 9,
}: ImageUploadHookProps) => {
  const reduxDispatch = useDispatch();

  const scope = `file-upload-${name}`;
  useListeners(uploadsListeners);

  const [autosave] = useContext(AutosaveContext);
  const [files, dispatch] = useReducer(UploadReducer, value || []);

  const setFieldValue = useSetFieldValue();

  const prevFiles = useRef(null);

  useEffect(() => {
    if (prevFiles.current && files && !isEqual(prevFiles.current, files)) {
      void setFieldValue(name, files);

      if (autosave) {
        autosave();
      }
    }

    prevFiles.current = files;
  }, [files, name, setFieldValue, autosave]);

  const handleImageReorder = useCallback((oldId: string, newId: string) => {
    dispatch({
      type: 'REORDER_IMAGE',
      oldId,
      newId,
    });
  }, []);

  const handleCropSuccess = useCallback((image: ImageUpload) => {
    dispatch({
      type: 'UPDATE_IMAGE_PATH',
      image,
    });
  }, [dispatch]);

  const handleCrop = useCallback((file: PublicImageUrl, crop: Crop) => {
    const options = {
      scope,
      targetRatio,
      callback: handleCropSuccess,
    };

    reduxDispatch(startCrop({
      file, name, crop, options,
    }));
  }, [reduxDispatch, name, handleCropSuccess, scope, targetRatio]);

  const handleLaunchCropDialog = useCallback((file: ImageUpload) => {
    Modal.show({
      title: 'Crop',
      buttons: [],
      body: (
        <Loader
          load={CropDialogLoader}
          file={file}
          handleCrop={handleCrop}
          targetRatio={targetRatio}
        />
      ),
    });
  }, [handleCrop, targetRatio]);

  const handleUploadSuccess = useCallback((image: ImageUpload) => {
    dispatch({
      type: 'ADD_IMAGE',
      image,
      multiple,
    });

    handleLaunchCropDialog(image);
  }, [dispatch, multiple, handleLaunchCropDialog]);

  const handleDrop = useCallback((uploadedFiles: File[]) => {
    const options = {
      scope,
      targetRatio,
      callback: handleUploadSuccess,
    };

    reduxDispatch(startUpload({ files: uploadedFiles, name, options }));
  }, [reduxDispatch, name, handleUploadSuccess, scope, targetRatio]);

  const handleImageRemove = useCallback((filePath: string) => {
    if (!disabled) {
      dispatch({
        type: 'REMOVE_IMAGE',
        filePath,
      });
    }
  }, [dispatch, disabled]);

  return {
    handleImageRemove,
    handleDrop,
    handleImageReorder,
    handleLaunchCropDialog,
    scope,
    files,
  };
};
